import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import { useState, useEffect } from 'react';
import apiService from '@/services/api';

const AdminProviderOnboardingPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [fetchedData, setFetchedData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.request('/admin/providers/onboarding', 'GET');
        if (response.success && response.data) {
          setFetchedData(response.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Handler: Approve onboarding
  const handleApproveOnboarding = async (providerId: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.request(`/admin/onboarding/${providerId}/approve`, 'POST', {
        approvedBy: localStorage.getItem('adminId'),
        approvedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Onboarding approved');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler: Reject onboarding
  const handleRejectOnboarding = async (providerId: string, reason: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.request(`/admin/onboarding/${providerId}/reject`, 'POST', {
        reason: reason,
        rejectedBy: localStorage.getItem('adminId'),
        rejectedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Onboarding rejected');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler: Request onboarding info
  const handleRequestOnboardingInfo = async (providerId: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.request(`/admin/onboarding/${providerId}/request-info`, 'POST', {
        requestedBy: localStorage.getItem('adminId'),
        requestedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Info request sent');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler: Export onboarding report
  const handleExportOnboardingReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.request('/admin/onboarding/report', 'GET');
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `onboarding-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ Report exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler: Bulk onboarding action
  const handleBulkOnboardingAction = async (action: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.request(`/admin/onboarding/bulk-${action}`, 'POST', {
        action: action,
        performedBy: localStorage.getItem('adminId'),
        performedAt: new Date().toISOString()
      });
      if (response.success) {
        alert(`✓ Bulk ${action} complete`);
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  const statusColor = (s: string) => {
    if (s === 'approved') return 'bg-green-100 text-green-800';
    if (s === 'pending') return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  return (
    <>
      <Helmet>
        <title>Provider Onboarding - Virewire Admin</title>
      </Helmet>
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm mb-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Provider Onboarding</h1>
            <p className="text-gray-600">Manage provider registration and onboarding workflows.</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase mb-1">Total</p>
              <p className="text-2xl font-bold text-gray-900">{fetchedData.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase mb-1">Approved</p>
              <p className="text-2xl font-bold text-green-600">{fetchedData.filter((p: any) => p.status === 'approved').length}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase mb-1">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{fetchedData.filter((p: any) => p.status === 'pending').length}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 uppercase mb-1">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">{fetchedData.filter((p: any) => p.status === 'in_progress').length}</p>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Providers</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-600">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-600">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-600">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-600">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-600">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {fetchedData.length === 0 ? (
                    <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">No providers found</td></tr>
                  ) : (
                    fetchedData.map((p: any) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{p.provider_name}</div>
                          <div className="text-xs text-gray-500">ID: {p.id}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{p.company_name || '—'}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-700">{p.email}</div>
                          {p.phone && <div className="text-xs text-gray-500">{p.phone}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColor(p.status)}`}>{p.status}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : '—'}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex gap-2 justify-end">
                            {p.status !== 'approved' && (
                              <button onClick={() => handleApproveOnboarding(p.id)} disabled={isLoading} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50">Approve</button>
                            )}
                            <button onClick={() => { const r = prompt('Reject reason:'); if (r) handleRejectOnboarding(p.id, r); }} disabled={isLoading} className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50">Reject</button>
                            <button onClick={() => handleRequestOnboardingInfo(p.id)} disabled={isLoading} className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 disabled:opacity-50">Request Info</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminProviderOnboardingPage;
